import { ComponentProps, ElementType } from 'react'
import { VariantProps, tv } from 'tailwind-variants'
import { SpinnerLoading } from '../SpinnerLoading'

const buttonVariants = tv({
  slots: {
    container:
      'rounded-md w-fit font-outfit text-white text-opacity-85 transition-colors delay-75 leading-5 flex items-center justify-center',
    icon: '',
  },
  variants: {
    colorScheme: {
      primary: {
        container:
          'hover:bg-gray-600 bg-gray-700 border border-white border-opacity-5',
      },
      secondary: {
        container:
          'bg-primary-600 hover:bg-primary-500 border border-primary-500',
        icon: 'text-white',
      },
      tertiary: {
        container: 'bg-white bg-opacity-5 border border-gray-800',
      },
      outline: {
        container:
          'border border-white border-opacity-25 enabled:hover:bg-opacity-5 hover:bg-white',
      },
      ghost: {
        container: 'bg-transparent hover:bg-opacity-5 hover:bg-white',
      },
      warning: {
        container:
          'hover:bg-primary-700 bg-primary-600 border border-white border-opacity-5',
      },
    },
    sizes: {
      base: {
        container: 'p-2 text-sm',
      },
      md: {
        container: 'px-4 py-3 text-sm',
      },
      lg: {
        container: 'px-6 py-4 text-base',
      },
    },
    full: {
      true: {
        container: 'w-full',
      },
    },
    isDisabled: {
      true: {
        container: 'opacity-50 cursor-not-allowed pointer-events-none',
      },
    },
  },
  defaultVariants: {
    colorScheme: 'primary',
    sizes: 'base',
  },
})

type TButtonProps = VariantProps<typeof buttonVariants> &
  ComponentProps<'button'> & {
    LeftElement?: ElementType
    RightElement?: ElementType
    isLoading?: boolean
  }

function LoadingState() {
  return (
    <div className="">
      <SpinnerLoading />
    </div>
  )
}

export const Button = ({
  children,
  colorScheme,
  LeftElement,
  RightElement,
  sizes,
  isDisabled,
  isLoading,
  full,
  className,
  ...rest
}: TButtonProps) => {
  const { container, icon } = buttonVariants({
    colorScheme,
    sizes,
    isDisabled,
    full,
  })

  return (
    <button
      disabled={isDisabled}
      {...rest}
      className={container({ className })}>
      {LeftElement && <LeftElement className={`mr-2 ${icon()}`} />}
      {isLoading ? <LoadingState /> : children}
      {RightElement && <RightElement className={`ml-2 ${icon()}`} />}
    </button>
  )
}
